import type { Dayjs, ManipulateType } from 'dayjs';

import {
  CHART_FREQUENCY,
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
} from '@/entities/chart/model/constants';
import { getTimezoneDayjs } from '@/utils/time';

import {
  type ResolvedSignalDateRange,
  SIGNAL_DATE_PRESET,
  SIGNAL_PERIOD,
  type SignalDateSelection,
  type SignalDateSelectionInput,
} from '../model';

type SignalDatePresetDefinition = {
  unit: SIGNAL_PERIOD;
  manipulateType: 'hour' | 'day';
  value: number;
  boundaryValue?: number;
  comparisonManipulateType?: 'hour' | 'day';
  comparisonValue?: number;
};

const SIGNAL_DATE_PRESET_DEFINITION: Record<
  SIGNAL_DATE_PRESET,
  SignalDatePresetDefinition
> = {
  [SIGNAL_DATE_PRESET.PAST_24_HOURS]: {
    unit: SIGNAL_PERIOD.HOURLY,
    manipulateType: 'hour',
    value: 24,
  },
  [SIGNAL_DATE_PRESET.PAST_3_DAYS]: {
    unit: SIGNAL_PERIOD.HOURLY,
    manipulateType: 'hour',
    value: 72,
    comparisonManipulateType: 'day',
    comparisonValue: 3,
  },
  [SIGNAL_DATE_PRESET.PAST_7_DAYS]: {
    unit: SIGNAL_PERIOD.DAILY,
    manipulateType: 'day',
    value: 7,
  },
  [SIGNAL_DATE_PRESET.PAST_30_DAYS]: {
    unit: SIGNAL_PERIOD.DAILY,
    manipulateType: 'day',
    value: 30,
  },
  [SIGNAL_DATE_PRESET.PAST_90_DAYS]: {
    unit: SIGNAL_PERIOD.DAILY,
    manipulateType: 'day',
    value: 90,
    boundaryValue: 89,
    comparisonValue: 90,
  },
};

export const DEFAULT_SIGNAL_DATE_SELECTION: SignalDateSelection = {
  type: 'preset',
  preset: SIGNAL_DATE_PRESET.PAST_7_DAYS,
};

export const toSignalDateSelection = (
  selection: SignalDateSelectionInput,
): SignalDateSelection =>
  typeof selection === 'string'
    ? { type: 'preset', preset: selection }
    : selection;

export const parseSignalDatePreset = (
  selection: SignalDateSelectionInput,
): {
  unit: SIGNAL_PERIOD;
  manipulateType: 'hour' | 'day';
  value: number;
} => {
  const normalizedSelection = toSignalDateSelection(selection);
  if (normalizedSelection.type === 'custom') {
    const start = getTimezoneDayjs(normalizedSelection.startDate).startOf('day');
    const end = getTimezoneDayjs(normalizedSelection.endDate).startOf('day');
    return {
      unit: SIGNAL_PERIOD.DAILY,
      manipulateType: 'day',
      value: end.diff(start, 'day') + 1,
    };
  }

  const preset = normalizedSelection.preset;
  const definition = SIGNAL_DATE_PRESET_DEFINITION[preset];
  if (!definition) {
    throw new Error(`Unknown signal date preset: ${preset}`);
  }

  const { unit, manipulateType, value } = definition;
  return { unit, manipulateType, value };
};

export const parseSignalDatePresetBoundary = (
  selection: SignalDateSelectionInput,
): {
  boundaryManipulateType: 'hour' | 'day';
  boundaryValue: number;
  comparisonManipulateType: 'hour' | 'day';
  comparisonValue: number;
} => {
  const normalizedSelection = toSignalDateSelection(selection);
  if (normalizedSelection.type === 'custom') {
    const { manipulateType, value } = parseSignalDatePreset(normalizedSelection);
    return {
      boundaryManipulateType: manipulateType,
      boundaryValue: value,
      comparisonManipulateType: manipulateType,
      comparisonValue: value,
    };
  }

  const definition = SIGNAL_DATE_PRESET_DEFINITION[normalizedSelection.preset];
  if (!definition) {
    throw new Error(
      `Unknown signal date preset: ${normalizedSelection.preset}`,
    );
  }

  const {
    manipulateType,
    value,
    boundaryValue,
    comparisonManipulateType,
    comparisonValue,
  } = definition;

  return {
    boundaryManipulateType: manipulateType,
    boundaryValue: boundaryValue ?? value,
    comparisonManipulateType: comparisonManipulateType ?? manipulateType,
    comparisonValue: comparisonValue ?? value,
  };
};

const getSelectedPeriodStart = (
  now: Dayjs,
  manipulateType: ManipulateType,
  value: number,
) => {
  // day preset은 day boundary로 맞춰야 selected(1x) count/modal/chart가 동일해집니다.
  return now.subtractVal(value, manipulateType).startOf(manipulateType);
};

export const getSignalDatePresetBucketCount = (
  selection: SignalDateSelectionInput,
): number => {
  const normalizedSelection = toSignalDateSelection(selection);
  const { manipulateType, value } = parseSignalDatePreset(normalizedSelection);
  if (normalizedSelection.type === 'custom') return value;
  const { boundaryValue } = parseSignalDatePresetBoundary(normalizedSelection);
  return manipulateType === 'hour' ? value : boundaryValue + 1;
};

export const resolveSignalDateRange = (
  selection: SignalDateSelectionInput,
): ResolvedSignalDateRange => {
  const normalizedSelection = toSignalDateSelection(selection);

  if (normalizedSelection.type === 'custom') {
    const selectedStart = getTimezoneDayjs(normalizedSelection.startDate).startOf(
      'day',
    );
    const selectedEnd = getTimezoneDayjs(normalizedSelection.endDate).endOf(
      'day',
    );
    const bucketCount = selectedEnd.startOf('day').diff(selectedStart, 'day') + 1;
    const comparisonStart = selectedStart
      .subtractVal(bucketCount, 'day')
      .startOf('day');
    const comparisonEnd = selectedStart.subtractVal(1, 'day').endOf('day');

    return {
      selectedStart: selectedStart.toJSON(),
      selectedEnd: selectedEnd.toJSON(),
      comparisonStart: comparisonStart.toJSON(),
      comparisonEnd: comparisonEnd.toJSON(),
      queryStart: comparisonStart.toJSON(),
      queryEnd: selectedEnd.toJSON(),
      period: SIGNAL_PERIOD.DAILY,
      manipulateType: 'day',
      bucketCount,
    };
  }

  const preset = normalizedSelection.preset;
  const now = getTimezoneDayjs();
  const {
    boundaryManipulateType,
    boundaryValue,
    comparisonManipulateType,
    comparisonValue,
  } = parseSignalDatePresetBoundary(preset);
  const selectedStart = getSelectedPeriodStart(
    now,
    boundaryManipulateType,
    boundaryValue,
  );
  const selectedEnd = now.endOf(boundaryManipulateType);
  const comparisonStart = selectedStart
    .subtractVal(comparisonValue, comparisonManipulateType)
    .startOf(boundaryManipulateType);
  const comparisonEnd = selectedStart
    .subtractVal(1, comparisonManipulateType)
    .endOf(boundaryManipulateType);
  const { unit } = parseSignalDatePreset(preset);

  return {
    selectedStart: selectedStart.toJSON(),
    selectedEnd: selectedEnd.toJSON(),
    comparisonStart: comparisonStart.toJSON(),
    comparisonEnd: comparisonEnd.toJSON(),
    queryStart: comparisonStart.toJSON(),
    queryEnd: selectedEnd.toJSON(),
    period: unit,
    manipulateType: boundaryManipulateType,
    bucketCount: getSignalDatePresetBucketCount(preset),
  };
};

/**
 * @description
 * datePreset의 값에 따라 시작일과 종료일을 반환합니다.
 * changes 계산을 위해 선택한 기간의 2배를 반환합니다.
 * 예: PAST_3_DAYS 선택 시 6일치 데이터 범위 반환
 */
export const getDateRangeBySignalDatePreset2x = (
  selection: SignalDateSelectionInput,
): [Dayjs, Dayjs] => {
  const range = resolveSignalDateRange(selection);
  return [getTimezoneDayjs(range.queryStart), getTimezoneDayjs(range.queryEnd)];
};

/**
 * @description
 * 사용자가 선택한 기간(1x)의 시작일과 종료일을 반환합니다.
 * UI에 표시할 날짜 범위용으로 사용합니다.
 * 예: PAST_3_DAYS 선택 시 최근 3일 범위 반환
 */
export const getSelectedPeriodDateRange1x = (
  selection: SignalDateSelectionInput,
): [Dayjs, Dayjs] => {
  const range = resolveSignalDateRange(selection);
  return [
    getTimezoneDayjs(range.selectedStart),
    getTimezoneDayjs(range.selectedEnd),
  ];
};

/**
 * @description
 * 선택 기간과 비교 기간의 날짜 범위를 포맷된 문자열로 반환합니다.
 * tooltip 등 UI 표시용으로 사용합니다.
 */
export const getPeriodDateRangeLabels = (
  selection: SignalDateSelectionInput,
): { selectedPeriod: string; comparisonPeriod: string } => {
  const range = resolveSignalDateRange(selection);
  const boundaryManipulateType = range.manipulateType;
  const dateFormat =
    boundaryManipulateType === 'hour' ? 'MM/DD HH:mm' : 'MM/DD';

  return {
    selectedPeriod: `${getTimezoneDayjs(range.selectedStart).format(dateFormat)} - ${getTimezoneDayjs(range.selectedEnd).format(dateFormat)}`,
    comparisonPeriod: `${getTimezoneDayjs(range.comparisonStart).format(dateFormat)} - ${getTimezoneDayjs(range.comparisonEnd).format(dateFormat)}`,
  };
};

export type IssueDateValue = { date: string; value: number };

export type PeriodCountResult = {
  /** 선택한 기간의 합계 */
  selectedPeriodCount: number;
  /** 비교 기간의 합계 */
  comparisonPeriodCount: number;
};

/**
 * @description
 * API 응답 values에서 선택 기간과 비교 기간의 feedback count를 계산합니다.
 * 날짜 기반으로 분리하여 dateMap 매핑 없이 직접 합계를 계산합니다.
 *
 * @example
 * PAST_3_DAYS 선택 시:
 * - selectedPeriodCount: 최근 3일 합계
 * - comparisonPeriodCount: 3~6일 전 합계
 */
export const calculatePeriodCounts = (
  values: IssueDateValue[],
  selectionOrConfig:
    | SignalDateSelectionInput
    | { manipulateType: ManipulateType; value: number },
): PeriodCountResult => {
  const isLegacyConfig =
    typeof selectionOrConfig === 'object' &&
    'manipulateType' in selectionOrConfig;
  let manipulateType: ManipulateType;
  let selectedPeriodStart: Dayjs;
  let selectedPeriodEnd: Dayjs;
  let comparisonPeriodStart: Dayjs | null;
  let comparisonPeriodEnd: Dayjs | null;

  if (isLegacyConfig) {
    manipulateType = selectionOrConfig.manipulateType;
    selectedPeriodStart = getSelectedPeriodStart(
      getTimezoneDayjs(),
      selectionOrConfig.manipulateType,
      selectionOrConfig.value,
    );
    selectedPeriodEnd = getTimezoneDayjs().endOf(manipulateType);
    comparisonPeriodStart = null;
    comparisonPeriodEnd = null;
  } else {
    const resolvedRange = resolveSignalDateRange(selectionOrConfig);
    manipulateType = resolvedRange.manipulateType;
    selectedPeriodStart = getTimezoneDayjs(resolvedRange.selectedStart);
    selectedPeriodEnd = getTimezoneDayjs(resolvedRange.selectedEnd);
    comparisonPeriodStart = getTimezoneDayjs(resolvedRange.comparisonStart);
    comparisonPeriodEnd = getTimezoneDayjs(resolvedRange.comparisonEnd);
  }

  let selectedPeriodCount = 0;
  let comparisonPeriodCount = 0;

  for (const item of values) {
    const itemDate = getTimezoneDayjs(item.date);
    const isInSelectedPeriod =
      (itemDate.isAfter(selectedPeriodStart) ||
        itemDate.isSame(selectedPeriodStart, manipulateType)) &&
      (itemDate.isBefore(selectedPeriodEnd) ||
        itemDate.isSame(selectedPeriodEnd, manipulateType));
    if (isInSelectedPeriod) {
      selectedPeriodCount += item.value;
    } else if (
      comparisonPeriodStart == null ||
      ((itemDate.isAfter(comparisonPeriodStart) ||
        itemDate.isSame(comparisonPeriodStart, manipulateType)) &&
        comparisonPeriodEnd != null &&
        (itemDate.isBefore(comparisonPeriodEnd) ||
          itemDate.isSame(comparisonPeriodEnd, manipulateType)))
    ) {
      comparisonPeriodCount += item.value;
    }
  }

  return {
    selectedPeriodCount,
    comparisonPeriodCount,
  };
};

export const convertDateValueToSignalDatePreset = (
  period: SIGNAL_PERIOD,
  periodValue: number,
): SIGNAL_DATE_PRESET => {
  const preset = Object.values(SIGNAL_DATE_PRESET).find((preset) => {
    const { unit, value } = parseSignalDatePreset(preset);
    return unit === period && value === periodValue;
  });
  if (!preset)
    throw new Error(
      `Cannot convert to SIGNAL_DATE_PRESET with period: ${period} and periodValue: ${periodValue}`,
    );
  return preset;
};

export const parseSignalPeriod = (period: string): SIGNAL_PERIOD => {
  const isValid = Object.values(SIGNAL_PERIOD).includes(
    period as SIGNAL_PERIOD,
  );
  if (!isValid) {
    throw new Error(`Invalid signal period: ${period}`);
  }
  return period as SIGNAL_PERIOD;
};

export const parseSignalPeriodValue = (value: string): number => {
  const parsed = Number(value);
  if (isNaN(parsed) || parsed <= 0) {
    throw new Error(`Invalid signal period value: ${value}`);
  }
  return parsed;
};

export const convertSignalDatePresetToChartTerm = (
  selection: SignalDateSelectionInput,
): ChartTermDef => {
  const normalizedSelection = toSignalDateSelection(selection);
  if (normalizedSelection.type === 'custom') {
    const range = resolveSignalDateRange(normalizedSelection);
    return {
      type: CHART_TERM_TYPE.FIXED,
      unit: CHART_TERM_UNIT.DAY,
      start_date: range.selectedStart,
      end_date: range.selectedEnd,
    };
  }

  const { unit } = parseSignalDatePreset(normalizedSelection);
  const value = getSignalDatePresetBucketCount(normalizedSelection);

  const unitMap = {
    [SIGNAL_PERIOD.HOURLY]: CHART_TERM_UNIT.HOUR,
    [SIGNAL_PERIOD.DAILY]: CHART_TERM_UNIT.DAY,
    [SIGNAL_PERIOD.WEEKLY]: CHART_TERM_UNIT.WEEK,
  };

  return {
    type: CHART_TERM_TYPE.PERIODIC,
    relative: CHART_PERIOD_RELATIVE.INCLUDE,
    value,
    unit: unitMap[unit],
  };
};

export const convertSignalDatePresetToChartFrequency = (
  selection: SignalDateSelectionInput,
): CHART_FREQUENCY => {
  const { unit } = parseSignalDatePreset(selection);
  const frequencyMap = {
    [SIGNAL_PERIOD.HOURLY]: CHART_FREQUENCY.HOUR,
    [SIGNAL_PERIOD.DAILY]: CHART_FREQUENCY.DAY,
    [SIGNAL_PERIOD.WEEKLY]: CHART_FREQUENCY.WEEK,
  };
  return frequencyMap[unit];
};

export const getPreviousPeriodDescription = (
  selection: SignalDateSelectionInput,
): string => {
  const { manipulateType, value } = parseSignalDatePreset(selection);
  return `${value} ${manipulateType}s`;
};

/**
 * Signal date preset을 alert duration(일) 값으로 변환
 * duration 최대값(7일)으로 클램핑
 */
export const signalDatePresetToDurationDays = (
  selection: SignalDateSelectionInput,
): number => {
  const { manipulateType, value } = parseSignalDatePreset(selection);
  const days = manipulateType === 'hour' ? Math.ceil(value / 24) : value;
  return Math.min(days, 7);
};

export const getPeriodLabel = (selection: SignalDateSelectionInput): string => {
  const { manipulateType, value } = parseSignalDatePreset(selection);
  const map: Record<string, string> = {
    day: `${value} Days`,
    week: `${value} Weeks`,
    hour: `${value} Hours`,
  };
  return map[manipulateType] || 'Period';
};
